import { useEffect, useState } from 'react'
import { UseFormReturn } from 'react-hook-form'
import { useChainId } from 'wagmi'

import { ConvertStablesFormFields } from '../types'
import { createTxOverview, TxOverview } from './createTxOverview'
import { ConvertStablesFormSchema } from './form/schema'
import { useConvertStablesForm } from './form/useConvertStablesForm'
import { getChainConfigEntry } from '@/config/chain'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { SwapObjective } from '@/features/actions/flavours/swap/types'
import { useActions } from '@/features/actions/logic/useActions'
import { ActionsViewState } from '@/features/actions/types'
import { PageState, PageStatus } from '@/features/dialogs/common/types'
import { useSavingsTokenRepository } from '@/features/savings/common/useSavingsTokenRepository'

export interface UseConvertStablesDialogResult {
  formFields: ConvertStablesFormFields
  form: UseFormReturn<ConvertStablesFormSchema>
  pageStatus: PageStatus
  txOverview: TxOverview
  actionsViewState: ActionsViewState
}

export function useConvertStablesDialog(): UseConvertStablesDialogResult {
  const chainId = useChainId()
  const chainConfig = getChainConfigEntry(chainId)
  const { account } = useConnectedAddress({ throwOnNotConnected: false })

  const tokenRepository = useSavingsTokenRepository({ chainId, account })

  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const { form, formValues, formFields, isDebouncing, isFormValid } = useConvertStablesForm({
    tokenRepository,
    psmStables: chainConfig.savings?.psmStables,
  })
  const objectives: SwapObjective[] = [{ type: 'swap', ...formValues }]
  const txOverview = createTxOverview({ ...formValues })
  const actionsEnabled = formValues.amount.gt(0) && isFormValid && !isDebouncing

  const { viewState: actionsViewState, reset: resetActions } = useActions({
    objectives,
    context: {
      tokenRepository,
      chainId,
    },
    onFinish: () => setPageStatus('success'),
    enabled: actionsEnabled,
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to reset actions when the form values change
  useEffect(() => {
    resetActions()
  }, [formValues.from.symbol, formValues.to.symbol, formValues.amount.toString(), resetActions])

  return {
    form,
    formFields,
    txOverview,
    pageStatus: {
      state: pageStatus,
      actionsEnabled,
      goToSuccessScreen: () => setPageStatus('success'),
    },
    actionsViewState,
  }
}
